import { Button } from '../../components/Button';
import { ContentParser } from '../../components/ContentParser';
import { NotificationBox } from '../../components/NotificationBox';
import { NotificationActions } from '../../components/NotificationBox/NotificationBox.styles';
import { PageHeading } from '../../components/PageHeading';
import { PanelCard } from '../../components/PanelCard';
import { SideImage } from '../../components/SideImage';
import { SkeletonCard } from '../../components/SkeletonCard';
import { StickyFooter } from '../../components/StickyFooter';
import { BookingTopBar } from '../../composites/BookingTopBar';
import { GuestDetailsPanel } from '../../composites/GuestDetailsPanel';
import { SplitLayout } from '../../composites/SplitLayout';
import { AddGuestRow, NotificationContainer, ScrollContainer } from './SpaClassBookingForm.styles';
import type { SpaClassBookingFormProps } from './SpaClassBookingForm.types';

export function SpaClassBookingForm({
  imageUrl,
  imageAlt,
  onBack,
  backLabel,
  progressStep,
  progressTotal,
  isLoading,
  telephoneOnly,
  unavailable,
  guests,
  maxGuests,
  onAddGuest,
  onRemoveGuest,
  onExistingGuestSelect,
  onGuestChange,
  titleOptions,
  genderOptions,
  stickyFooter,
  labels,
}: SpaClassBookingFormProps) {
  const {
    title,
    subtitle,
    stickyFooter: stickyFooterLabels,
    addGuestLabel = 'Add guest',
    removeGuestLabel = 'Remove',
  } = labels;
  const canAddGuest = !!onAddGuest && (maxGuests === undefined || guests.length < maxGuests);

  const showTelephoneOnly = telephoneOnly && !!labels.telephoneOnly;
  const unavailableLabels = unavailable ? labels.unavailable : undefined;

  const selectedGuestIds = guests
    .map((g) => g.selectedGuestId)
    .filter((id): id is string => id !== undefined);

  const right =
    showTelephoneOnly || unavailableLabels ? (
      <NotificationContainer>
        <PageHeading title={title} subtitle={subtitle} />
        {showTelephoneOnly && (
          <NotificationBox
            title={labels.telephoneOnly!.title}
            subtitle={<ContentParser text={labels.telephoneOnly!.subtitle} />}
          />
        )}
        {unavailableLabels && (
          <NotificationBox
            title={unavailableLabels.title}
            subtitle={<ContentParser text={unavailableLabels.subtitle} />}
            cta={
              <NotificationActions $stacked={!!unavailableLabels.secondaryCtaLabel}>
                <Button
                  variant="primary"
                  size={unavailableLabels.secondaryCtaLabel ? 'large' : undefined}
                  fullWidth={!!unavailableLabels.secondaryCtaLabel}
                  onClick={unavailableLabels.onCtaClick ?? onBack}
                >
                  {unavailableLabels.ctaLabel}
                </Button>
                {unavailableLabels.secondaryCtaLabel && (
                  <Button
                    variant="secondary"
                    size="large"
                    fullWidth
                    onClick={unavailableLabels.onSecondaryCtaClick ?? onBack}
                  >
                    {unavailableLabels.secondaryCtaLabel}
                  </Button>
                )}
              </NotificationActions>
            }
          />
        )}
      </NotificationContainer>
    ) : (
      <>
        <ScrollContainer>
          <PageHeading title={title} subtitle={subtitle} />

          {isLoading
            ? guests.map((g) => <SkeletonCard key={g.guestNumber} variant="simple" />)
            : guests.map((guest, guestIndex) => {
                const disabledGuestIds = selectedGuestIds.filter(
                  (id) => id !== guest.selectedGuestId
                );

                return (
                  <PanelCard key={guest.guestNumber}>
                    <GuestDetailsPanel
                      guestNumber={guest.guestNumber}
                      showHeader={guestIndex > 0}
                      onRemove={
                        guestIndex > 0 && onRemoveGuest
                          ? () => onRemoveGuest(guestIndex)
                          : undefined
                      }
                      existingGuests={guest.existingGuests}
                      selectedGuestId={guest.selectedGuestId}
                      disabledGuestIds={disabledGuestIds}
                      onExistingGuestSelect={(guestId) =>
                        onExistingGuestSelect(guestIndex, guestId)
                      }
                      values={guest.values}
                      onChange={(field, value) => onGuestChange(guestIndex, field, value)}
                      titleOptions={titleOptions}
                      genderOptions={genderOptions}
                      errors={guest.errors}
                      labels={{ ...labels.guestDetails, removeLabel: removeGuestLabel }}
                    />
                  </PanelCard>
                );
              })}
          {!isLoading && canAddGuest && (
            <AddGuestRow>
              <Button type="button" variant="secondary" size="small" onClick={onAddGuest}>
                {addGuestLabel}
              </Button>
            </AddGuestRow>
          )}
        </ScrollContainer>
        {stickyFooter && stickyFooterLabels && (
          <StickyFooter
            {...stickyFooter}
            totalLabel={stickyFooterLabels.totalLabel}
            ctaLabel={stickyFooterLabels.ctaLabel}
          />
        )}
      </>
    );

  return (
    <SplitLayout
      hideLeftOnMobile
      left={<SideImage src={imageUrl} alt={imageAlt} sticky onBack={onBack} />}
      right={
        <>
          <BookingTopBar
            desktopHidden
            inset={false}
            onBack={onBack}
            backLabel={backLabel}
            progressStep={progressStep}
            progressTotal={progressTotal}
          />
          {right}
        </>
      }
      contentGap="0px"
      contentMaxWidth="100%"
    />
  );
}
